import { useState } from 'react'
import { ChevronDown, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useIsSuperUser } from '@/hooks/useIsSuperUser'
import { cn } from '@/lib/utils'
import { formatFtwe, formatRateField, rateChangeSentences } from '../lib/rates'
import {
  FTWE_LABEL,
  RATE_FIELDS,
  type RateCardSnapshot,
  type RateChange,
} from '../types'
import { ChangeRateCardDialog } from './ChangeRateCardDialog'

interface RateCardCardProps {
  /** The rate card in force now. Relay sends one with every rate null when
   *  the org has none. */
  rateCard: RateCardSnapshot | null
  /** Each rate a new card moved during the period, oldest first; none when
   *  the rates held all period. */
  rateChanges: RateChange[]
  isCurrent: boolean
}

/** The org's rate card, for Careforce global admins. For the current month,
 *  the card in force now, the way to change it, and the rates a new card moved
 *  this month; for a closed month, the rates that moved in it and how they
 *  were billed, and nothing when they held all month. Kept off the printed
 *  page, which goes to the customer. Collapsed until an admin opens it, since
 *  it is only needed when a rate is in question. */
export function RateCardCard({
  rateCard,
  rateChanges,
  isCurrent,
}: RateCardCardProps) {
  const isSuperUser = useIsSuperUser()
  const [open, setOpen] = useState(false)
  const [changeOpen, setChangeOpen] = useState(false)
  if (!isSuperUser) return null
  if (!isCurrent && rateChanges.length === 0) return null
  const hasCard =
    rateCard != null && RATE_FIELDS.some(({ key }) => rateCard[key] != null)

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section
        aria-labelledby="billing-rate-card-heading"
        className="hidden-on-print bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
        <h2
          id="billing-rate-card-heading"
          className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
          <CollapsibleTrigger className="flex w-full items-center gap-1.5 uppercase tracking-widest">
            <Lock aria-hidden="true" className="size-3.5" />
            Rate card
            <ChevronDown
              aria-hidden="true"
              className={cn(
                'ml-auto size-4 transition-transform',
                open && 'rotate-180'
              )}
            />
          </CollapsibleTrigger>
        </h2>

        <CollapsibleContent className="mt-4">
          {isCurrent && (
            <>
              <div className="mb-4 flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  title="Careforce global admins only"
                  onClick={() => setChangeOpen(true)}>
                  <Lock aria-hidden="true" />
                  Change rate card
                </Button>
              </div>

              {hasCard ? (
                <dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
                  {RATE_FIELDS.map(({ key, label }) => (
                    <div key={key} className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="tabular-nums">
                        {rateCard[key] == null
                          ? 'Not set'
                          : formatRateField(key, rateCard[key])}
                      </dd>
                    </div>
                  ))}
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">{FTWE_LABEL}</dt>
                    <dd className="tabular-nums">
                      {rateCard.ftwe_tier == null ||
                      rateCard.ftwe_multiplier == null
                        ? 'Not set'
                        : formatFtwe(
                            rateCard.ftwe_tier,
                            rateCard.ftwe_multiplier
                          )}
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="text-sm text-muted-foreground">No rate card</p>
              )}
            </>
          )}

          {rateChanges.length > 0 && (
            <div
              role="note"
              className={cn(
                'text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2',
                isCurrent && 'mt-4'
              )}>
              {rateChangeSentences(rateChanges).map((sentence) => (
                <p key={sentence}>{sentence}</p>
              ))}
            </div>
          )}

          <p className="mt-4 text-xs text-muted-foreground">
            Calls, sessions, documents and rubric evaluations bill at the rates
            in force when they happen. The Connection charge and the round-up to
            a whole minute use the card in force on the last day of the month.
            The FTWE multiplier is the minutes of agent work that count as one
            person-day in the full-time-worker estimate; it bills nothing.
          </p>
        </CollapsibleContent>

        {isCurrent && (
          <ChangeRateCardDialog
            open={changeOpen}
            onOpenChange={setChangeOpen}
            rateCard={rateCard}
          />
        )}
      </section>
    </Collapsible>
  )
}
